import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminDashboard from './AdminDashboard';
import { getCurrentUser, subscribeToAuthState, logout, type User } from '../services/graphql/authService';
import { Layout } from './Layout';
import { useServiceKey } from '../hooks/useServiceKey';
import { useRoleContext, RoleContextType } from '../context/RoleContext';
import { Activity } from 'lucide-react';
import ProgressDialog from './ProgressDialog';

/**
 * AdminAccessPage component provides access to admin dashboards based on user role,
 * relying on RoleContext for role information.
 */
const AdminAccessPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const { serviceConfig } = useServiceKey();
  const { userProfile, loading: roleLoading, error: roleError }: RoleContextType = useRoleContext();
  const [activeTab, setActiveTab] = useState(0);

  const [connectionStatus, setConnectionStatus] = useState<{
    auth: boolean;
    database: boolean;
    isChecking: boolean;
  }>({
    auth: true,
    database: true,
    isChecking: false
  });

  // Get current user from GraphQL auth
  useEffect(() => {
    // Initial fetch of user
    getCurrentUser()
      .then((user) => setCurrentUser(user))
      .catch(() => setCurrentUser(null));

    // Subscribe to auth state changes
    const unsubscribe = subscribeToAuthState((user) => {
      setCurrentUser(user);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Function to check connection health
  const checkConnection = async () => {
    setConnectionStatus(prev => ({ ...prev, isChecking: true }));
    try {
      console.log('[AdminAccess] Checking API connection health');

      // Check auth
      let authOk = false;
      try {
        await getCurrentUser();
        authOk = true;
      } catch {
        // Auth check can fail if not logged in - that's ok
        authOk = true;
      }

      // For database check, we'll assume it's OK if auth works
      // In a real implementation, you'd have a health endpoint
      const dbOk = authOk;

      setConnectionStatus({
        auth: authOk,
        database: dbOk,
        isChecking: false
      });

      console.log('[AdminAccess] Connection check complete:', { auth: authOk, database: dbOk });
    } catch (err) {
      console.error('[AdminAccess] Error checking connection:', err);
      setConnectionStatus({
        auth: false,
        database: false,
        isChecking: false
      });
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Navigate to the corresponding route based on tab index
    if (newValue === 0) {
      navigate(`/${serviceConfig.serviceId}/system/admin/all-bookings`);
    } else if (newValue === 1) {
      navigate(`/${serviceConfig.serviceId}/system/admin`);
    }
  };

  // Show loading state while RoleContext is loading or currentUser is not yet determined
  if (roleLoading || currentUser === undefined) {
    return (
      <Layout currentUser={currentUser} serviceConfig={serviceConfig}>
        <ProgressDialog isOpen={true} message="Checking permissions..." subMessage="Please wait while we check your admin access." />
      </Layout>
    );
  }

  // Determine roles from userProfile (from RoleContext)
  const isSuperAdminRole = userProfile?.role === 'superadmin';
  const isServiceAdminRole = userProfile?.role === 'serviceprovideradmin';

  // Show error state if RoleContext has an error or if user is not an admin
  // Allow serviceprovideradmin even if they are not superadmin
  const accessDeniedError = roleError || (!isSuperAdminRole && !isServiceAdminRole && currentUser ? 'You do not have permission to access admin features.' : null);

  if (accessDeniedError && !isServiceAdminRole) {
    return (
      <Layout currentUser={currentUser} onToggleDebug={() => {}} serviceConfig={serviceConfig}>
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md bg-background-paper p-8 rounded-lg shadow-md text-foreground">
            <h2 className="text-xl font-bold text-destructive mb-4">Access Denied</h2>
            <p className="text-foreground-muted mb-6">{accessDeniedError}</p>
            {/* Debug information section */}
            <div className="bg-secondary p-4 mb-6 rounded text-secondary-foreground">
              <h3 className="font-semibold mb-2">Debug Information:</h3>
              <p className="text-sm mb-2">
                <strong>User ID:</strong> {currentUser?.id || 'Not logged in'}
              </p>
              <p className="text-sm mb-2">
                <strong>Email:</strong> {currentUser?.email || 'N/A'}
              </p>
              <p className="text-sm mb-1">
                <strong>Context Role:</strong> {userProfile?.role || 'N/A'} (Loading: {roleLoading.toString()})
              </p>
              <ul className="list-disc list-inside text-sm ml-2 mb-2">
                <li>SuperAdmin: {isSuperAdminRole ? <span className="text-success">Yes</span> : <span className="text-destructive">No</span>}</li>
                <li>Service Admin: {isServiceAdminRole ? <span className="text-success">Yes</span> : <span className="text-destructive">No</span>}</li>
              </ul>
              <p className="text-sm mb-2">
                <strong>Note:</strong> You need to have either a 'superadmin' or 'serviceprovideradmin' role in the user_profiles collection.
              </p>
              {/* Connection Status Section */}
              <div className="mt-4 p-3 bg-background-paper rounded-md text-foreground">
                <h4 className="font-medium mb-2 flex items-center">
                  <Activity className="h-4 w-4 mr-2 text-info" />
                  Connection Status
                </h4>
                <div className="flex items-center space-x-4 mb-2">
                  <div className="text-sm">
                    Auth Service:
                    <span className={`ml-1 font-medium ${connectionStatus.auth ? 'text-success' : 'text-destructive'}`}>
                      {connectionStatus.auth ? 'Online' : 'Offline'}
                    </span>
                  </div>
                  <div className="text-sm">
                    Database Service:
                    <span className={`ml-1 font-medium ${connectionStatus.database ? 'text-success' : 'text-destructive'}`}>
                      {connectionStatus.database ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={checkConnection}
                  disabled={connectionStatus.isChecking}
                  className="w-full text-center px-3 py-1 bg-info text-info-foreground text-sm rounded hover:bg-info/90 transition-colors disabled:opacity-50"
                >
                  {connectionStatus.isChecking ? 'Checking Connection...' : 'Check API Connection'}
                </button>
              </div>
            </div>

            <div className="flex flex-col space-y-4">
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors"
              >
                Go Back
              </button>

              <button
                onClick={() => window.location.href = `/${serviceConfig.serviceId || ''}`}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded hover:bg-secondary/90 transition-colors"
              >
                Return to Home
              </button>

              <button
                onClick={() => {
                  logout().then(() => {
                    window.location.href = `/${serviceConfig.serviceId || ''}`;
                  });
                }}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded hover:bg-destructive/90 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Check if we're on the base-bookings route for service provider admins
  const isBaseBookingsRoute = location.pathname.includes('/base-bookings');

  // If user is serviceprovideradmin and trying to access admin dashboard, redirect to base-bookings
  if (isServiceAdminRole && !isBaseBookingsRoute && !isSuperAdminRole) {
    navigate(`/${serviceConfig.serviceId}/base-bookings`);
    return null;
  }

  // If user is on base-bookings route (regardless of role), show the BaseBookingsPage component
  if (isBaseBookingsRoute) {
    // Dynamic import to avoid circular dependency
    const BaseBookingsPage = React.lazy(() => import('./BaseBookingsPage'));

    return (
      <Layout currentUser={currentUser} onToggleDebug={() => {}} serviceConfig={serviceConfig}>
        <React.Suspense fallback={
          <ProgressDialog isOpen={true} message="Loading bookings..." subMessage="Please wait while we fetch your bookings." />
        }>
          <BaseBookingsPage />
        </React.Suspense>
      </Layout>
    );
  }

  // Show the appropriate dashboard based on role
  return (
    <Layout currentUser={currentUser} onToggleDebug={() => {}} serviceConfig={serviceConfig}>
      <div className="container mx-auto p-4">
        <div className="admin-content">
          <AdminDashboard currentUser={currentUser} />
        </div>
      </div>
    </Layout>
  );
};

export default AdminAccessPage;
